import * as React from 'react';
import { Loader2, Download, FileText } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useExports } from '@/hooks/exports/use-exports';
import { useExportDownloadUrl } from '@/hooks/exports/use-export-download-url';
import { triggerDownload } from '@/lib/download';
import { useToast } from '@/hooks/use-toast';
import { EXPORT_TYPE_CODES, type ExportTypeCode, type ExportDto } from '@cutlyft/zod-schemas';

const TYPE_LABELS: Record<ExportTypeCode, string> = {
  CUTTING_LIST_XLSX: 'Cutting List (XLSX)',
  GLASS_LIST_XLSX: 'Glass List (XLSX)',
  CLIENT_QUOTE_PDF: 'Client Quote (PDF)',
  LAYOUT_PDF: 'Layout (PDF)',
};

const FILTER_OPTIONS = [
  { value: 'ALL', label: 'All types' },
  ...EXPORT_TYPE_CODES.map((code) => ({ value: code, label: TYPE_LABELS[code] })),
];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function DownloadCell({ exportId }: { exportId: number }) {
  const { toast } = useToast();
  const { isLoading, refetch } = useExportDownloadUrl(exportId);

  const handleDownload = async () => {
    const result = await refetch();
    const url = result.data;
    if (url) {
      triggerDownload(url);
    } else {
      toast({ variant: 'destructive', title: 'Could not get download URL' });
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={handleDownload} disabled={isLoading}>
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

function ExportCard({ exp }: { exp: ExportDto }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 py-3 px-4">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="h-4 w-4 text-fg-muted shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{TYPE_LABELS[exp.exportType]}</p>
            <p className="text-xs text-fg-muted">{formatDate(exp.createdDate)}</p>
          </div>
          {exp.projectId && (
            <Badge variant="outline" className="text-xs">Project #{exp.projectId}</Badge>
          )}
          {exp.runId && (
            <Badge variant="outline" className="text-xs">Run #{exp.runId}</Badge>
          )}
        </div>
        <DownloadCell exportId={exp.id} />
      </CardContent>
    </Card>
  );
}

export default function ExportsPage() {
  const [typeFilter, setTypeFilter] = React.useState<ExportTypeCode | 'ALL'>('ALL');
  const [cursor, setCursor] = React.useState<number | undefined>();

  const { data, isLoading } = useExports({
    exportType: typeFilter === 'ALL' ? undefined : typeFilter,
    cursor,
    limit: 20,
  });
  const exports_ = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-fg-primary">Exports</h1>
        <p className="text-fg-secondary mt-1">
          Download previously generated exports. New exports are generated from a project's detail page.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-52">
          <Combobox
            options={FILTER_OPTIONS}
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter((v ?? 'ALL') as ExportTypeCode | 'ALL');
              setCursor(undefined);
            }}
            placeholder="All types"
            searchPlaceholder="Search type..."
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
        </div>
      ) : exports_.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex h-32 items-center justify-center text-fg-muted text-sm">
            No exports yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {exports_.map((exp) => (
            <ExportCard key={exp.id} exp={exp} />
          ))}
        </div>
      )}

      {data?.hasMore && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCursor(data.nextCursor ?? undefined)}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
